import { ReactNode } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { AccountCircle } from '@mui/icons-material'
import { Stack } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import DrawerPortal from '@/components/drawer/DrawerPortal'
import ErrorBoundary from '@/components/error-boundary/ErrorBoundary'
import NavigationRailPortal from '@/components/navigation-rail/NavigationRailPortal'
import RelevantIDsDialog from '@/components/relevant-ids/RelevantIDsDialog'
import useRegisterRelevantID from '@/components/relevant-ids/useRegisterRelevantID'
import ScrollContainer from '@/components/scroll-container/ScrollContainer'
import CommandPaletteDialog from '../command-palette/CommandPaletteDialog'

type AppLayoutProps = {
  header?: ReactNode
  children: ReactNode
  isLite?: boolean
}

export default function AppLayout({
  header,
  children,
  isLite = false,
}: AppLayoutProps) {
  const isSuperUser = useIsSuperUser()
  const commandPaletteOverlay = useOverlay()
  const relevantIDsOverlay = useOverlay()
  const { currentAccount } = useCurrentAccountContext()

  useRegisterRelevantID(
    currentAccount
      ? {
          group: 'Top Level',
          type: 'Account',
          label: currentAccount.name,
          id: currentAccount.id,
          icon: AccountCircle,
          color: 'teal',
        }
      : null,
  )

  useHotkeys(
    'mod+k',
    () => {
      commandPaletteOverlay.open()
    },
    {
      preventDefault: true, // Prevent the default browser behavior where it focuses the address bar
    },
  )

  useHotkeys(
    'mod+i',
    () => {
      relevantIDsOverlay.toggle()
    },
    {
      enabled: isSuperUser,
      preventDefault: true,
    },
  )
  return (
    <ErrorBoundary>
      <Stack sx={{ height: '100vh' }}>
        {header}

        <Stack direction="row" sx={{ flexGrow: 1, minHeight: 0 }}>
          {!isLite && (
            <>
              <NavigationRailPortal />
              {/* Drawer Portal - Left side only */}
              <DrawerPortal />
            </>
          )}
          {/* Page Content */}
          <ErrorBoundary>
            <ScrollContainer>{children}</ScrollContainer>
          </ErrorBoundary>

          {/* Drawer Portal - right side */}
          {!isLite && <DrawerPortal anchor="right" />}
        </Stack>
      </Stack>
      <CommandPaletteDialog overlay={commandPaletteOverlay} isLite={isLite} />
      {isSuperUser && <RelevantIDsDialog overlay={relevantIDsOverlay} />}
    </ErrorBoundary>
  )
}
